import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import showToast from "../components/Toast";

// The stored session is the API's auth payload: account fields, a token, and
// the linked player profile (null until the account claims one).
const storedUser = () => JSON.parse(localStorage.getItem("user")) || null;

const authHeader = () => {
  const user = storedUser();
  if (!user) throw new Error("Not logged in.");
  return { authorization: "Bearer " + user.token };
};

// ------------------------------- login ----------------------------------
export const login = async (credentials) => {
  const response = await axios.post("/api/v1/user/login", credentials);
  return response.data.data;
};

export const useLogin = () => {
  const navigate = useNavigate();
  return useMutation(login, {
    onSuccess: (session) => {
      localStorage.setItem("user", JSON.stringify(session));
      navigate("/");
      showToast("User logged in Successfully!", "success");
    },
    onError: (error) => {
      showToast(error.response?.data?.error || "Login failed!", "error");
    },
  });
};

// ------------------------------ register --------------------------------
// Registration creates both the account and its player profile. The profile
// starts inactive, so say so rather than implying they are ranked already.
export const register = async (userData) => {
  const response = await axios.post("/api/v1/user/register", userData);
  return response.data.data;
};

export const useRegister = () => {
  const navigate = useNavigate();
  return useMutation(register, {
    onSuccess: (session, variables) => {
      // The claim branch created no player, so send them straight on to find
      // theirs — logged in, because the candidate search is authenticated.
      if (variables.claimExisting) {
        localStorage.setItem("user", JSON.stringify(session));
        navigate("/claim");
        showToast("Account created. Now find your profile.", "success");
        return;
      }

      navigate("/login");
      showToast(
        "Registered! An admin will activate your profile before it appears in the rankings.",
        "success"
      );
    },
    onError: (error) => {
      showToast(error.response?.data?.error || "Registration failed!", "error");
    },
  });
};

// ------------------------------- logout ---------------------------------
export const logout = async () => {
  return localStorage.removeItem("user");
};

export const useLogout = () => {
  const navigate = useNavigate();
  const mutation = useMutation(logout);

  const handleLogout = async () => {
    await mutation.mutateAsync();
    navigate("/");
    showToast("User logged out Successfully!", "success");
  };

  return { ...mutation, handleLogout };
};

// --------------------------- own profile --------------------------------
const getOwnProfile = async () => {
  const response = await axios.get("/api/v1/user/me", {
    headers: authHeader(),
  });
  return response.data.data; // { _id, email, role, player }
};

export const useOwnProfile = () => {
  return useQuery("user", getOwnProfile);
};

// Editable profile fields all live on the player, not the account.
export const updateOwnProfile = async (playerData) => {
  const response = await axios.put("/api/v1/user/me", playerData, {
    headers: authHeader(),
  });
  return response.data.data;
};

export const useUpdateOwnProfile = () => {
  const queryClient = useQueryClient();
  return useMutation(updateOwnProfile, {
    onSuccess: (player) => {
      queryClient.invalidateQueries("user");

      // Keep the cached session in step so the navbar avatar and any other
      // localStorage reader see the update without a re-login.
      const user = storedUser();
      if (user) {
        localStorage.setItem("user", JSON.stringify({ ...user, player }));
      }

      showToast("Profile updated successfully", "success");
    },
    onError: (error) => {
      showToast(error.response?.data?.error || "Update failed!", "error");
    },
  });
};

// --------------------------- public players -----------------------------
// Accepts an id or a slug, so /players/omar-al-mansoori works.
export const getPlayer = async (idOrSlug) => {
  const response = await axios.get(`/api/v1/public/players/${idOrSlug}`);
  return response.data.data;
};

export const usePlayer = (idOrSlug) => {
  return useQuery(["player", idOrSlug], () => getPlayer(idOrSlug), {
    enabled: Boolean(idOrSlug),
    refetchOnWindowFocus: false,
  });
};

// ------------------------------ rankings --------------------------------
export const getRankings = async (
  page = 1,
  search = "",
  gender = "",
  nationality = "",
  limit = ""
) => {
  const response = await axios.get(`/api/v1/public/rankings`, {
    params: {
      search,
      gender,
      nationality,
      page,
      limit,
    },
  });

  return response.data;
};

export const useRankings = (page, search, gender, nationality, limit) => {
  return useQuery(
    ["rankings", page, search, gender, nationality, limit],
    () => getRankings(page, search, gender, nationality, limit),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true, // To prevent the previous data from disappearing while fetching the new data
    }
  );
};
